import React from 'react';
import PropTypes from 'prop-types';

import IArchive from '@meronex/icons/fi/FiArchive';
import IAudit from '@meronex/icons/fi/FiClipboard';
import ICsv from '@meronex/icons/fi/FiDownload';
import IQRCode from '@meronex/icons/fi/FiTag';
import IMenu from '@meronex/icons/md/MdMenu';
import IPlus from '@meronex/icons/fi/FiPlus';
import IFolder from '@meronex/icons/fi/FiFolder';
import IEdit from '@meronex/icons/fi/FiEdit2';
import IChart from '@meronex/icons/fi/FiBarChart2';
import IBox from '@meronex/icons/fi/FiPackage';
import MdClose from '@meronex/icons/md/MdClose';

import { useTheme } from '@material-ui/core/styles';

import { storesMng } from '../../api/storesMng';

function StoreDrawer(props) {
  const theme = useTheme();
  const isDark = theme.palette.type === 'dark';
  const {
    selectedStore,
    onAddItem,
    onAuditStore,
    onOpen,
    onExportToCsv,
    onClose,
    onOpenArchives,
    onGenerateLabels,
    onAnalytics,
    onEditStore,
    onArchiveStore,
    permissions,
    hideFab,
  } = props;

  const [openDrawer, setOpenDrawer] = React.useState(false);
  const [archivedCount, setArchivedCount] = React.useState(0);

  React.useEffect(() => {
    if (selectedStore) {
      fetArchivesCount();
    }
  }, [openDrawer]);

  const _onClose = () => {
    setOpenDrawer(false);
    onClose();
  };

  const fetArchivesCount = async () => {
    const archivedCount = await storesMng.getArchivedStoreItemsCount(
      selectedStore._id
    );
    setArchivedCount(archivedCount);
  };

  // Touch handling for swipe-to-dismiss
  const touchStartY = React.useRef(0);
  const onTouchStart = (e) => {
    touchStartY.current = e.touches[0].clientY;
  };
  const onTouchEnd = (e) => {
    const deltaY = e.changedTouches[0].clientY - touchStartY.current;
    if (deltaY > 80) {
      _onClose();
    }
  };

  // Shared styles matching desktop panel
  const accentColor = isDark ? 'rgba(99, 102, 241, 0.8)' : '#6366f1';
  const mutedColor = isDark ? '#64748b' : '#94a3b8';
  const textColor = isDark ? 'rgba(255,255,255,0.8)' : '#475569';
  const cardBg = isDark ? 'rgba(255,255,255,0.03)' : '#fff';
  const cardBorder = isDark ? 'rgba(255,255,255,0.06)' : '#e2e8f0';
  const hoverBg = isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc';

  const cardStyle = {
    borderRadius: '16px',
    border: `1px solid ${cardBorder}`,
    backgroundColor: cardBg,
    padding: '20px',
    marginBottom: '12px',
  };

  const sectionLabel = {
    fontSize: '11px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: accentColor,
    marginBottom: '12px',
  };

  const DrawerItem = ({ icon, label, onClick, disabled, highlight }) => (
    <div
      onClick={disabled ? undefined : onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        padding: '12px 14px',
        borderRadius: '10px',
        cursor: disabled ? 'default' : 'pointer',
        fontSize: '14px',
        color: disabled ? mutedColor : (highlight ? accentColor : textColor),
        opacity: disabled ? 0.4 : 1,
        transition: 'background-color 0.15s ease',
      }}
      onMouseEnter={(e) => {
        if (!disabled) e.currentTarget.style.backgroundColor = hoverBg;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'transparent';
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center', color: highlight ? accentColor : mutedColor }}>
        {icon}
      </span>
      <span style={{ fontWeight: 500 }}>{label}</span>
    </div>
  );

  return (
    <div>
      {!hideFab && (
        <div
          onClick={() => {
            setOpenDrawer(true);
            onOpen();
          }}
          style={{
            position: 'fixed',
            right: '20px',
            bottom: '24px',
            width: '52px',
            height: '52px',
            borderRadius: '16px',
            background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            cursor: 'pointer',
            boxShadow: '0 4px 16px rgba(99, 102, 241, 0.4)',
            zIndex: 100,
            transition: 'transform 0.15s ease, box-shadow 0.15s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.05)';
            e.currentTarget.style.boxShadow = '0 6px 20px rgba(99, 102, 241, 0.5)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 4px 16px rgba(99, 102, 241, 0.4)';
          }}
        >
          <IMenu size={22} />
        </div>
      )}

      {openDrawer && selectedStore && (
        <div
          onClick={_onClose}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1300,
            backgroundColor: 'rgba(0,0,0,0.5)',
            backdropFilter: 'blur(2px)',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              maxHeight: '85vh',
              overflowY: 'auto',
              backgroundColor: isDark ? '#1a1a2e' : '#f1f5f9',
              borderRadius: '20px 20px 0 0',
              padding: '12px 16px',
              paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
              animation: 'slideUpDrawer 0.25s ease-out',
            }}
          >
            <style>{`
              @keyframes slideUpDrawer {
                from { transform: translateY(100%); }
                to { transform: translateY(0); }
              }
            `}</style>

            {/* Drag handle */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: '8px',
            }}>
              <div style={{
                width: '36px',
                height: '4px',
                borderRadius: '2px',
                backgroundColor: isDark ? 'rgba(255,255,255,0.2)' : '#cbd5e1',
              }} />
            </div>

            {/* Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '16px',
              padding: '0 4px',
            }}>
              <span style={{
                fontSize: '16px',
                fontWeight: 600,
                color: isDark ? '#e2e8f0' : '#0f172a',
              }}>
                Store Options
              </span>
              <div
                onClick={_onClose}
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: isDark ? '#94a3b8' : '#64748b',
                  transition: 'background-color 0.15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.06)' : '#e2e8f0'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <MdClose size={20} />
              </div>
            </div>

            {/* Store Info */}
            <div style={cardStyle}>
              <div style={sectionLabel}>Store Info</div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '10px 14px',
                borderRadius: '10px',
                backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc',
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  backgroundColor: isDark ? 'rgba(99,102,241,0.15)' : '#eef2ff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: accentColor,
                  flexShrink: 0,
                }}>
                  <IBox size={18} />
                </div>
                <div>
                  <div style={{ fontSize: '16px', fontWeight: 600, color: isDark ? '#e2e8f0' : '#0f172a' }}>
                    {selectedStore.itemCount || 0} items
                  </div>
                  <div style={{ fontSize: '12px', color: mutedColor }}>
                    In inventory
                  </div>
                </div>
              </div>
            </div>

            {/* Reports */}
            <div style={cardStyle}>
              <div style={sectionLabel}>Reports</div>
              <DrawerItem icon={<IChart size={18} />} label="Store Analytics" onClick={() => { onAnalytics(); _onClose(); }} />
              <DrawerItem icon={<ICsv size={18} />} label="Generate Reports" onClick={() => { onExportToCsv(); _onClose(); }} />
              <DrawerItem icon={<IQRCode size={18} />} label="Generate Labels" onClick={() => { onGenerateLabels(); _onClose(); }} />
            </div>

            {/* Store */}
            <div style={cardStyle}>
              <div style={sectionLabel}>Store</div>
              {permissions.auditStore && (
                <DrawerItem icon={<IAudit size={18} />} label="Audit Store" onClick={() => { onAuditStore(); _onClose(); }} />
              )}
              <DrawerItem icon={<IEdit size={18} />} label="Edit Store" onClick={() => { onEditStore(); _onClose(); }} />
              <DrawerItem icon={<IArchive size={18} />} label="Archive Store" onClick={() => { onArchiveStore(); _onClose(); }} />
            </div>

            {/* Items */}
            <div style={cardStyle}>
              <div style={sectionLabel}>Items</div>
              {!selectedStore.productsStore && (
                <DrawerItem icon={<IPlus size={18} />} label="Add Item" highlight onClick={() => { onAddItem(); _onClose(); }} />
              )}
              <DrawerItem icon={<IFolder size={18} />} label="Archived Items" onClick={() => { onOpenArchives(); _onClose(); }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
StoreDrawer.propTypes = {
  selectedStore: PropTypes.object,
  onAddItem: PropTypes.func,
  onOpen: PropTypes.func,
  onOpenArchives: PropTypes.func,
  onAuditStore: PropTypes.func,
  onExportToCsv: PropTypes.func,
  onClose: PropTypes.func,
  hideFab: PropTypes.bool,
  permissions: PropTypes.object,
  onGenerateLabels: PropTypes.func,
  onAnalytics: PropTypes.func,
  onEditStore: PropTypes.func,
  onArchiveStore: PropTypes.func,
};

StoreDrawer.defaultProps = {
  onAddItem: () => {},
  onAuditStore: () => {},
  onExportToCsv: () => {},
  onGenerateLabels: () => {},
  onAnalytics: () => {},
  onEditStore: () => {},
  onArchiveStore: () => {},
  onOpen: () => {},
  onClose: () => {},
};
export default StoreDrawer;
